import { ChangeDetectorRef, Component, ElementRef, input, OnInit, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ApiSettings, PaginationConstants } from '../../../../core/constants/constants';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { ExpenseListSortOptions } from '../../../../core/enums/sort-options.enums';
import { ExpenseModel } from '../../../../core/models/data/expense.model';
import { ExpenseFilter } from '../../../../core/filters/expense.filter';
import { PaginatedListModel } from '../../../../core/models/service/paginated-list.model';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { FinancialService } from '../../../../core/services/financial/financial.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { FeatureFlag } from '../../../../core/enums/feature-flags.enums';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { OrgDatePipe } from '../../../../core/pipes/org-date/org-date.pipe';
import { CurrencyDisplayPipe } from '../../../../core/pipes/currency/currency.pipe';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { Router } from '@angular/router';
import { DataEntityTypes, ExpenseStatus } from '../../../../core/enums/data.enums';
import { FinancialsPermission } from '../../../../core/enums/permissions.enums';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { BalancesMessagingConstants } from '../../../../core/constants/messaging.constants';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { EditExpenseModalComponent } from '../edit-expense-modal/edit-expense-modal.component';

@Component({
  selector: 'ms-expense-list-table',
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    PaginationComponent,
    InViewPortDirective,
    NoValuePipe,
    OrgDatePipe,
    CurrencyDisplayPipe,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective,
    TooltipDirective,
    IfHasPermissionDirective,
    StatusBadgeComponent,
    ConfirmationModalComponent,
    InfoModalComponent,
    EditExpenseModalComponent
  ],
  templateUrl: './expense-list-table.component.html',
  styleUrl: './expense-list-table.component.scss',
  host: { "style": "display:contents" }
})
export class ExpenseListTableComponent extends BaseListPageClass implements OnInit {

  //#region Properties & Signals
  public filter = input<ExpenseFilter | undefined>(undefined);
  public orderByInput = input<string | undefined>(undefined);

  public orderByChanged = output<string>();

  public expenses = signal<ExpenseModel[]>([]);
  public expensesFilter: ExpenseFilter = ListConstants.EXPENSES_FILTER_DEFAULT();

  public ExpenseListSortOptions = ExpenseListSortOptions;
  public ExpenseStatus = ExpenseStatus;
  public FinancialsPermission = FinancialsPermission;
  public DataEntityTypes = DataEntityTypes;

  /** The row an Edit or Void action is currently acting on. */
  private targetedExpense?: ExpenseModel;

  @ViewChild('expenseVoidConfirmationRef') voidConfirmationRef!: ConfirmationModalComponent;
  @ViewChild('expenseVoidBlockedRef') voidBlockedRef!: ConfirmationModalComponent;
  @ViewChild('expenseListInfoRef') infoRef!: InfoModalComponent;
  @ViewChild('editExpenseModalRef') editExpenseModalRef!: EditExpenseModalComponent;

  public skeletonExpenses: ExpenseModel[] = [];

  public isSubmitting = false;

  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;
  //#endregion Properties & Signals

  constructor(
    private financialService: FinancialService,
    private changeDetector: ChangeDetectorRef,
    public userSessionService: UserSessionService,
    private bootstrapService: BootstrapService,
    private routerService: Router
  ) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    this.sortOptions = [...ListConstants.EXPENSES_SORT_OPTIONS()];

    const incomingFilter = this.filter();
    this.expensesFilter = incomingFilter
      ? { ...incomingFilter }
      : ListConstants.EXPENSES_FILTER_DEFAULT();

    this.orderBy = this.orderByInput() ?? ListConstants.EXPENSES_ORDERBY_DEFAULT();

    if (this.userSessionService.activeOrganization()) {
      this.expensesFilter.organizationIds = [this.userSessionService.activeOrganization()!.organizationId];
    }

    this.createSkeletonExpenses();
    await this.loadExpenses(false);
  }

  private createSkeletonExpenses(): void {
    this.skeletonExpenses = Array.from({ length: this.take }, (_, index) => ({
      expenseId: index,
      organizationId: 0,
      vendorName: 'Loading...',
      datePaid: '2025-01-01',
      amount: 0,
      currency: { currencyId: 1, name: 'USD', symbol: '$', minorUnits: 100 },
      referenceNumber: '',
      description: '',
      expenseAccount: { chartOfAccountId: 0, organizationId: 0, accountNumber: 0, category: 'Expenses', name: 'Loading...', description: '', isActive: true },
      status: ExpenseStatus.Pending
    } as ExpenseModel));
  }

  //#region Data Loading
  public get displayExpenses(): ExpenseModel[] {
    if (this.isLoading && (!this.expenses() || this.expenses().length === 0)) {
      return this.skeletonExpenses;
    }
    return this.expenses();
  }

  public async loadExpenses(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (append === false) { this.resetPagination(); }

    const incomingFilter = this.filter();
    if (incomingFilter) {
      this.expensesFilter = { ...incomingFilter };
    }

    const orgSettings = await this.userSessionService.getOrganizationSettings();
    this.expensesFilter.timeZoneId =
      orgSettings?.timeZoneId ?? ApiSettings.DEFAULT_ORG_TIMEZONE_FALLBACK().id;

    const response: ServiceResultModel<PaginatedListModel<ExpenseModel>> =
      await this.financialService.searchExpenses(this.expensesFilter, this.orderBy, this.skip(), this.take);

    this.totalCount = response?.data?.total ?? 0;
    this.hasNext = response?.data?.hasNext ?? false;

    if (response?.data?.data) {
      if (append === true) {
        this.expenses.set(this.expenses().concat(response.data.data));
      } else {
        this.expenses.set(response.data.data);
      }
    }

    this.isLoading = false;
    this.changeDetector.detectChanges();
  }

  public async loadMore(): Promise<void> {
    await this.loadExpenses(true);
  }

  public filterApplied(): void {
    this.loadExpenses();
  }

  public loadMoreInView(): void {
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }
  //#endregion Data Loading

  //#region Sorting & Pagination
  public toggleSortDirection(columnName: string): void {
    const columnSortMap: Record<string, [ExpenseListSortOptions, ExpenseListSortOptions]> = {
      "Vendor": [ExpenseListSortOptions.VendorNameAsc, ExpenseListSortOptions.VendorNameDesc],
      "Reference #": [ExpenseListSortOptions.ReferenceNumberAsc, ExpenseListSortOptions.ReferenceNumberDesc],
      "Expense Account": [ExpenseListSortOptions.ExpenseAccountAsc, ExpenseListSortOptions.ExpenseAccountDesc],
      "Amount": [ExpenseListSortOptions.AmountAsc, ExpenseListSortOptions.AmountDesc],
      "Date Paid": [ExpenseListSortOptions.DatePaidAsc, ExpenseListSortOptions.DatePaidDesc],
      "Status": [ExpenseListSortOptions.StatusAsc, ExpenseListSortOptions.StatusDesc]
    };

    const [ascOption, descOption] = columnSortMap[columnName]
      || [ExpenseListSortOptions.DatePaidAsc, ExpenseListSortOptions.DatePaidDesc];

    let sortDirection: ExpenseListSortOptions;

    if (this.orderBy === descOption) {
      sortDirection = ascOption;
    } else if (this.orderBy === ascOption) {
      sortDirection = descOption;
    } else {
      sortDirection = ascOption;
    }

    this.setSortOrder(sortDirection);
  }

  public setSortOrder(sortOption: string) {
    this.orderBy = sortOption;
    this.orderByChanged.emit(sortOption);
    this.filterApplied();
  }

  public jumpToTop(element: string): void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;

      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }
  //#endregion Sorting & Pagination

  //#region Row Actions

  // Editing a Cleared expense is deliberate: none of the three editable fields reaches the ledger,
  // so correcting a payee typo must not require undoing a bank reconciliation match.
  public canEdit(expense: ExpenseModel): boolean {
    return expense.status !== ExpenseStatus.Voided;
  }

  // Cleared stays enabled on purpose — confirmExpenseVoid explains that the match must be undone
  // first, which a greyed-out item could not. Only a second void is genuinely unavailable.
  public canVoid(expense: ExpenseModel): boolean {
    return expense.status !== ExpenseStatus.Voided;
  }

  public editExpense(expense: ExpenseModel): void {
    this.editExpenseModalRef.open(expense);
  }

  public confirmExpenseVoid(expense: ExpenseModel): void {
    this.targetedExpense = expense;

    // A reconciled expense's accrual pair is already pointed at by a live match line, so the API
    // refuses the void until that match is undone. Say so rather than letting it 422.
    if (expense.status === ExpenseStatus.Cleared) {
      // Without bank rec there is nowhere to send them to undo the match, so this is information
      // rather than a confirmation - the "Go to Bank Reconciliation" action would only 403.
      if (!this.userSessionService.isFeatureEnabled(FeatureFlag.BankReconciliation)) {
        this.infoRef.modalType.set(ModalTypes.Information);
        this.infoRef.message.set(BalancesMessagingConstants.EXPENSE_VOID_BLOCKED_RECONCILED_NO_BANK_REC());
        this.bootstrapService.manageModal('#expenseListInfoModal', ModalActions.Show);
        return;
      }

      this.voidBlockedRef.modalType.set(ModalTypes.Information);
      this.voidBlockedRef.message.set(BalancesMessagingConstants.EXPENSE_VOID_BLOCKED_RECONCILED());
      this.bootstrapService.manageModal('#expenseVoidBlockedModal', ModalActions.Show);
      return;
    }

    this.voidConfirmationRef.modalType.set(ModalTypes.Question);
    this.voidConfirmationRef.message.set(BalancesMessagingConstants.EXPENSE_VOID_CONFIRMATION());
    this.voidConfirmationRef.enableNotes.set(true);
    this.voidConfirmationRef.isNoteRequired.set(false);

    this.bootstrapService.manageModal('#expenseVoidConfirmationModal', ModalActions.Show);
  }

  public async confirmedExpenseVoid(note?: string): Promise<void> {
    const expense = this.targetedExpense;
    if (!expense) { return; }

    this.isSubmitting = true;
    this.voidConfirmationRef.isSubmitting.set(true);

    const response = await this.financialService.voidExpense(expense.expenseId, note);

    if (response.success && response.data) {
      this.infoRef.modalType.set(ModalTypes.Success);
      this.applyExpenseUpdate(response.data);
    } else {
      this.infoRef.modalType.set(ModalTypes.Failure);
    }

    this.isSubmitting = false;
    this.voidConfirmationRef.isSubmitting.set(false);
    this.infoRef.message.set(response.message || BalancesMessagingConstants.EXPENSE_VOID_FAILURE());
    this.bootstrapService.manageModal('#expenseVoidConfirmationModal', ModalActions.Hide);
    this.bootstrapService.manageModal('#expenseListInfoModal', ModalActions.Show);
  }

  public cancelExpenseVoid(): void {
    this.bootstrapService.manageModal('#expenseVoidConfirmationModal', ModalActions.Hide);
  }

  public goToBankReconciliation(): void {
    this.bootstrapService.manageModal('#expenseVoidBlockedModal', ModalActions.Hide);
    this.routerService.navigate(['/bank-reconciliation']);
  }

  public dismissVoidBlocked(): void {
    this.bootstrapService.manageModal('#expenseVoidBlockedModal', ModalActions.Hide);
  }

  public infoModalConfirmed(): void {
    this.bootstrapService.manageModal('#expenseListInfoModal', ModalActions.Hide);
  }

  /**
   * Both mutations hand back the updated expense, so patch the row rather than reloading — a reload
   * resets pagination and drops every page the reader has scrolled in. A row that no longer matches
   * an active status filter leaves the list instead.
   */
  public applyExpenseUpdate(updated: ExpenseModel): void {
    const statuses = this.expensesFilter.statuses;
    const stillMatches = !statuses?.length || statuses.includes(updated.status);

    if (stillMatches) {
      this.expenses.update(list =>
        list.map(expense => expense.expenseId === updated.expenseId ? updated : expense));
    } else {
      this.expenses.update(list => list.filter(expense => expense.expenseId !== updated.expenseId));
      this.totalCount = Math.max(0, this.totalCount - 1);
    }

    this.changeDetector.detectChanges();
  }

  //#endregion Row Actions
}

